import "./Comment.scss";
import classNames from "classnames";
import {useEffect, useState} from "react";
import { TextSelection } from "prosemirror-state"
import {formatTime} from "../../utils/Utils";
import Button from "../Button/Button";
import Icon, {checkIcon, editIcon, trashIcon} from "../Icon/Icon";
import Avatar from "../Avatar/Avatar";
import CommentActions from "../../api/CommentActions";
import useUser from "../../contexts/userContext/useUser";

const Comment = ({comment, posY, onResolve, onEdit, onDelete, isEditing, checkpointId, editor}) => {
    const {id, description, createdAt: time, user, resolved} = comment;
    const {user: currentUser} = useUser();
    const [activeCommentId, setActiveCommentId] = useState(null);
    const formattedTime = formatTime(time);
    const isOwnComment = !!currentUser?.id && currentUser.id === user?.id;

    const resolveComment = () => CommentActions.resolve({id, checkpoint: {id: checkpointId}}, onResolve);

    const selectComment = () => {
        const {view} = editor;
        const {doc, tr} = view.state;

        let from = null;
        let to = null;

        doc.descendants((node, pos) => {
            const isTable =
                node.type.name === "variableTable" &&
                node.attrs.commentId === id;

            if (isTable) {
                from = pos;
                to = pos + node.nodeSize;
                return false;
            }

            const hasMark = node.marks?.some(
                m => m.type.name === "comment" && m.attrs.commentId === id
            );

            if (hasMark) {
                if (from === null) from = pos;
                to = pos + node.nodeSize;
            }
        })

        if (from === null) return;

        view.dispatch(
            tr.setSelection(TextSelection.create(doc, from, to))
        );
        view.focus();
    }

    useEffect(() => {
        if (!editor) return;

        const updateActiveComment = () => {
            const {from, to} = editor.state.selection;
            let foundCommentId = null;

            editor.state.doc.nodesBetween(from, to === from ? from : to, (node) => {
                if (foundCommentId) return false;

                if (node.attrs?.commentId) {
                    foundCommentId = node.attrs.commentId;
                    return false;
                }

                const commentMark = node.marks?.find(
                    mark => mark.type.name === "comment"
                );

                if (commentMark) {
                    foundCommentId = commentMark.attrs.commentId;
                    return false;
                }
            })

            setActiveCommentId(foundCommentId);
        }

        editor.on("selectionUpdate", updateActiveComment);

        return () => {
            editor.off("selectionUpdate", updateActiveComment);
        }
    }, [editor]);

    return (
        <div
            className={classNames("comment", {resolved: resolved, active: activeCommentId === id, editing: isEditing})}
            style={{top: posY}}
            onClick={selectComment}
        >
            <div className="user-info">
                <Avatar name={user?.name} surname={user?.surname} color={user?.color} url={user?.image?.url}/>

                <div className="info">
                    <div className="username">{user?.name} {user?.surname}</div>
                    <div className="time">{formattedTime}</div>
                </div>

                <div className="comment-actions">
                    {
                        isOwnComment &&
                        <Button
                            variant="action"
                            className="delete-comment"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(comment);
                            }}
                        >
                            <Icon svg={trashIcon}/>
                        </Button>
                    }
                    {
                        !resolved && isOwnComment &&
                        <Button
                            variant="action"
                            className="edit"
                            onClick={() => onEdit(comment)}
                        >
                            <Icon svg={editIcon}/>
                        </Button>
                    }
                    {
                        !resolved &&
                        <Button
                            variant="action"
                            className="resolve"
                            onClick={resolveComment}
                        >
                            <Icon svg={checkIcon}/>
                        </Button>
                    }
                </div>
            </div>
            <div className="description">{description}</div>
        </div>
    )
}

export default Comment